import { useCallback } from 'react';
import type { TFunction } from 'i18next';
import {
  extractRoadReference,
  isAlreadyTranslatedRoadName,
} from '@/utils/roadUtils';
import { roadTypeFromRef, formatRoadDisplayName } from '@/utils/roads';

/**
 * GPS-first road mapping. Turns a raw GPS/reverse-geocoded suggestion into the
 * value shown in the road field. When the suggestion contains a recognizable
 * Polish road reference (e.g. `DK7`, `A4`), it is formatted into the localized
 * display name via the shared formatter. Otherwise the suggestion is passed
 * through unchanged (best-effort — GPS always wins).
 */
export function useRoadMapping(t: TFunction) {
  const mapGPSToRoad = useCallback(
    (suggestion: string): string => {
      if (!suggestion?.trim()) return '';

      // Already a formatted road name (e.g. "Droga krajowa DK7")? Pass it through
      // so we don't re-extract + re-translate it. Mirrors the same guard in the
      // canonical `roadUtils.formatRoadDisplayName`.
      if (isAlreadyTranslatedRoadName(suggestion)) {
        return suggestion;
      }

      const roadRef = extractRoadReference(suggestion);
      if (roadRef) {
        return formatRoadDisplayName(roadRef, roadTypeFromRef(roadRef), t);
      }

      // Not a recognizable road ref — preserve the original suggestion.
      return suggestion;
    },
    [t]
  );

  return {
    mapGPSToRoad,
    extractRoadReference,
  };
}
